import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams } from '@tanstack/react-router'
import { api } from '@/lib/api-client'
import { PageHeader } from '@/components/ui/page-header'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { FilterBar, type FilterDef } from '@/components/data/filter-bar'
import { formatCurrency, formatPercent, percent } from '@roi365/shared'
import { formatDate } from '@/lib/utils'
import { toast } from 'sonner'
import { useState } from 'react'
import { Eye, EyeOff, Save, X, Pencil } from 'lucide-react'

interface License {
  id: string
  sku_part_number: string
  display_name: string
  custom_display_name: string | null
  total_units: number
  assigned_units: number
  available_units: number
  monthly_cost_per_unit: number | null
  currency: string
  hidden: number
  m365_tenant_name: string | null
  catalog_price: number | null
  expires_at: string | null
}

const filters: FilterDef[] = [
  { key: 'search', label: 'Licencia', type: 'search', placeholder: 'Buscar por nombre o SKU...' },
  {
    key: 'visibility', label: 'Visibilidad', type: 'select',
    options: [{ value: 'visible', label: 'Visibles' }, { value: 'hidden', label: 'Ocultas' }],
  },
]

export function AdminClientLicensesPage() {
  const { clientId } = useParams({ strict: false }) as { clientId: string }
  const queryClient = useQueryClient()
  const [filterValues, setFilterValues] = useState<Record<string, string>>({})
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editForm, setEditForm] = useState({ customDisplayName: '', monthlyCostPerUnit: '', expiresAt: '' })

  const { data: tenant } = useQuery({
    queryKey: ['admin', 'tenants', clientId],
    queryFn: () => api.get<{ name: string }>(`/admin/tenants/${clientId}`),
  })

  const { data: licenses, isLoading } = useQuery({
    queryKey: ['admin', 'tenants', clientId, 'licenses'],
    queryFn: () => api.get<License[]>(`/admin/tenants/${clientId}/licenses`),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.put(`/admin/licenses/${id}`, data),
    onSuccess: () => {
      toast.success('Licencia actualizada')
      queryClient.invalidateQueries({ queryKey: ['admin', 'tenants', clientId, 'licenses'] })
      setEditingId(null)
    },
    onError: (err) => toast.error((err as Error).message),
  })

  const toggleHidden = (lic: License) => {
    updateMutation.mutate({ id: lic.id, data: { hidden: !lic.hidden } })
  }

  const startEdit = (lic: License) => {
    setEditingId(lic.id)
    setEditForm({
      customDisplayName: lic.custom_display_name ?? '',
      monthlyCostPerUnit: lic.monthly_cost_per_unit?.toString() ?? '',
      expiresAt: lic.expires_at ? lic.expires_at.slice(0, 10) : '',
    })
  }

  const saveEdit = (id: string) => {
    updateMutation.mutate({
      id,
      data: {
        customDisplayName: editForm.customDisplayName || null,
        monthlyCostPerUnit: editForm.monthlyCostPerUnit ? parseFloat(editForm.monthlyCostPerUnit) : null,
        expiresAt: editForm.expiresAt || null,
      },
    })
  }

  // Filter
  let filtered = licenses ?? []
  if (filterValues.search) {
    const s = filterValues.search.toLowerCase()
    filtered = filtered.filter(
      (l) =>
        l.display_name.toLowerCase().includes(s) ||
        l.sku_part_number.toLowerCase().includes(s) ||
        (l.custom_display_name ?? '').toLowerCase().includes(s),
    )
  }
  if (filterValues.visibility === 'visible') filtered = filtered.filter((l) => !l.hidden)
  if (filterValues.visibility === 'hidden') filtered = filtered.filter((l) => l.hidden)

  const totalCost = filtered.filter((l) => !l.hidden).reduce((acc, l) => acc + (l.monthly_cost_per_unit ?? 0) * l.assigned_units, 0)
  const hiddenCount = (licenses ?? []).filter((l) => l.hidden).length

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Licencias — ${tenant?.name ?? ''}`}
        actions={
          <Button variant="secondary" onClick={() => window.location.href = `/admin/clients/${clientId}`}>
            Volver al cliente
          </Button>
        }
      />

      {/* Summary */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Total tipo de licencias</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{(licenses ?? []).length}</p>
        </Card>
        <Card>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Coste mensual (visibles)</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{formatCurrency(totalCost)}</p>
        </Card>
        <Card>
          <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>Licencias ocultas</p>
          <p className="mt-1 text-2xl font-bold tabular-nums">{hiddenCount}</p>
        </Card>
      </div>

      {/* Filters */}
      <FilterBar
        filters={filters}
        values={filterValues}
        onChange={(k, v) => setFilterValues((prev) => ({ ...prev, [k]: v }))}
        onClear={() => setFilterValues({})}
      />

      {/* Table */}
      <Card padding={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'var(--color-gray-50)' }}>
                {['Licencia', 'Nombre personalizado', 'Unidades', 'Uso', 'Precio/ud/mes', 'Vence', 'Visibilidad', ''].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider"
                    style={{ color: 'var(--color-text-secondary)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                [...Array(5)].map((_, i) => (
                  <tr key={i}>
                    <td colSpan={8} className="px-4 py-4">
                      <div className="h-10 animate-pulse rounded" style={{ backgroundColor: 'var(--color-gray-100)' }} />
                    </td>
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center" style={{ color: 'var(--color-text-tertiary)' }}>
                    Sin licencias
                  </td>
                </tr>
              ) : (
                filtered.map((lic) => {
                  const isEditing = editingId === lic.id

                  return (
                    <tr
                      key={lic.id}
                      className="border-t"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: lic.hidden ? 'var(--color-gray-50)' : 'var(--color-surface)',
                        opacity: lic.hidden ? 0.6 : 1,
                      }}
                    >
                      {/* Licencia (nombre original + SKU) */}
                      <td className="px-4 py-3">
                        <div>
                          <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                            {lic.display_name}
                          </p>
                          <p className="mt-0.5 font-mono text-xs" style={{ color: 'var(--color-text-tertiary)' }}>
                            {lic.sku_part_number}
                          </p>
                        </div>
                      </td>

                      {/* Nombre personalizado */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input type="text" value={editForm.customDisplayName}
                            onChange={(e) => setEditForm((f) => ({ ...f, customDisplayName: e.target.value }))}
                            placeholder={lic.display_name}
                            className="w-full rounded-md border px-3 py-2 text-sm font-medium"
                            style={{ borderColor: 'var(--color-border-focus)', minWidth: '200px' }} />
                        ) : (
                          lic.custom_display_name ? (
                            <span className="text-sm font-medium" style={{ color: 'var(--color-brand-secondary)' }}>
                              {lic.custom_display_name}
                            </span>
                          ) : (
                            <span className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>Sin personalizar</span>
                          )
                        )}
                      </td>

                      {/* Unidades */}
                      <td className="px-4 py-3 tabular-nums">
                        {lic.assigned_units} / {lic.total_units}
                      </td>

                      {/* Uso */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="h-2 w-20 rounded-full" style={{ backgroundColor: 'var(--color-gray-200)' }}>
                            <div className="h-2 rounded-full"
                              style={{
                                width: `${Math.min(percent(lic.assigned_units, lic.total_units), 100)}%`,
                                backgroundColor: percent(lic.assigned_units, lic.total_units) > 90
                                  ? 'var(--color-warning)' : 'var(--color-brand-secondary)',
                              }} />
                          </div>
                          <span className="text-xs tabular-nums" style={{ color: 'var(--color-text-tertiary)' }}>
                            {formatPercent(percent(lic.assigned_units, lic.total_units))}
                          </span>
                        </div>
                      </td>

                      {/* Precio cliente */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" value={editForm.monthlyCostPerUnit}
                              onChange={(e) => setEditForm((f) => ({ ...f, monthlyCostPerUnit: e.target.value }))}
                              placeholder="0.00"
                              className="w-24 rounded-md border px-3 py-2 text-sm tabular-nums font-medium"
                              style={{ borderColor: 'var(--color-border-focus)' }} />
                            <span className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>EUR</span>
                          </div>
                        ) : (
                          <span className="tabular-nums font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                            {lic.monthly_cost_per_unit !== null ? formatCurrency(lic.monthly_cost_per_unit) : '—'}
                          </span>
                        )}
                      </td>

                      {/* Fecha de vencimiento */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <input
                            type="date"
                            value={editForm.expiresAt}
                            onChange={(e) => setEditForm((f) => ({ ...f, expiresAt: e.target.value }))}
                            className="rounded-md border px-3 py-2 text-sm tabular-nums"
                            style={{ borderColor: 'var(--color-border-focus)' }}
                          />
                        ) : lic.expires_at ? (
                          (() => {
                            const days = Math.ceil((new Date(lic.expires_at).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
                            const color = days < 0 ? 'var(--color-error)' : days < 30 ? 'var(--color-warning)' : 'var(--color-text-primary)'
                            return (
                              <span className="text-sm tabular-nums" style={{ color }}>
                                {formatDate(lic.expires_at)}
                              </span>
                            )
                          })()
                        ) : (
                          <span className="text-sm" style={{ color: 'var(--color-text-tertiary)' }}>—</span>
                        )}
                      </td>

                      {/* Visibilidad toggle */}
                      <td className="px-4 py-3">
                        <button onClick={() => toggleHidden(lic)}
                          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors"
                          style={{
                            backgroundColor: lic.hidden ? 'var(--color-error-light)' : 'var(--color-success-light)',
                            color: lic.hidden ? 'var(--color-error-dark)' : 'var(--color-success-dark)',
                          }}>
                          {lic.hidden ? <><EyeOff className="h-3.5 w-3.5" /> Oculta</> : <><Eye className="h-3.5 w-3.5" /> Visible</>}
                        </button>
                      </td>

                      {/* Acciones */}
                      <td className="px-4 py-3">
                        {isEditing ? (
                          <div className="flex gap-1">
                            <Button variant="primary" size="sm" onClick={() => saveEdit(lic.id)} loading={updateMutation.isPending}>
                              <Save className="h-3.5 w-3.5" /> Guardar
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        ) : (
                          <Button variant="secondary" size="sm" onClick={() => startEdit(lic)}>
                            <Pencil className="h-3.5 w-3.5" /> Editar
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
